import { extends as _extends } from '../../_virtual/_rollupPluginBabelHelpers.js';
import { inlineMediaQueryMixin, Box } from '@zillow/constellation';
import React, { useCallback, forwardRef } from 'react';
import styled from 'styled-components';
import Source from '../Source/Source.js';
import StyledVideo from '../Video/Video.js';

// Base video styling shared by all variants
var BaseBackgroundStyledVideo = /*#__PURE__*/styled(StyledVideo).withConfig({
  componentId: "sc-2vrk1d-0"
})(["&&&{pointer-events:none;position:absolute;top:0;right:0;bottom:0;left:0;", " width:100%;height:100%;", "}"], inlineMediaQueryMixin('size', function (mediaProps) {
  return "object-fit: " + mediaProps.size + ";";
}), inlineMediaQueryMixin('position', function (mediaProps) {
  return "object-position: " + mediaProps.position + ";";
}));

// Mobile viewport video - only visible on mobile screens
var MobileViewportVideo = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
    ref: ref,
    as: BaseBackgroundStyledVideo,
    sx: {
      '&&': {
        display: props.hideOnMobile ? 'none' : 'block',
        '@media $md': {
          display: 'none'
        }
      }
    }
  }));
});
MobileViewportVideo.displayName = 'MobileViewportVideo';

// If src is set, then visible on tablet and mobile if mobile has no src.
var TabletViewportVideo = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
    ref: ref,
    as: BaseBackgroundStyledVideo,
    sx: {
      '&&': {
        display: props.hideOnMobile ? 'none' : 'block',
        '@media $md': {
          display: props.hideOnTablet ? 'none' : 'block'
        },
        '@media $lg': {
          display: props.hideOnTablet ? 'none' : 'block'
        },
        '@media $xl': {
          display: 'none'
        }
      }
    }
  }));
});
TabletViewportVideo.displayName = 'TabletViewportVideo';

// If src is set, then visible on desktop, and cascades down to tablet if no tablet.src and if no tablet.src and no mobile.src then it cascades to show on mobile.
var DesktopViewportVideo = /*#__PURE__*/forwardRef(function (props, ref) {
  return /*#__PURE__*/React.createElement(Box, _extends({}, props, {
    ref: ref,
    as: BaseBackgroundStyledVideo,
    sx: {
      '&&': {
        display: props.hideOnMobile ? 'none' : 'block',
        '@media $md': {
          display: props.hideOnTablet ? 'none' : 'block'
        },
        '@media $xl': {
          display: props.hideOnDesktop ? 'none' : 'block'
        }
      }
    }
  }));
});
DesktopViewportVideo.displayName = 'DesktopViewportVideo';
/**
 * This component displays a background video that can have different sources
 * for different viewport sizes. The component uses a mobile-first approach:
 *
 * 1. The default/desktop video is always shown unless hidden by a more specific video
 * 2. Tablet and mobile videos are conditionally rendered based on their sources
 * 3. When a specific viewport has a source, it hides the default video for that viewport
 *
 * This ensures there's always a video visible, with the most appropriate source for
 * the current viewport size.
 */
var GroupBackgroundVideo = function GroupBackgroundVideo(_ref) {
  var _source$default, _source$lg_lte, _source$sm_lte;
  var _ref$position = _ref.position,
    position = _ref$position === void 0 ? {
      default: '50% 50%'
    } : _ref$position,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? {
      default: 'cover'
    } : _ref$size,
    source = _ref.source,
    _ref$visible = _ref.visible,
    visible = _ref$visible === void 0 ? {
      default: true,
      lg_lte: true,
      sm_lte: true
    } : _ref$visible;
  var muteVideoOnMountRef = useMuteVideoOnMountRef();

  // Get sources based on visibility settings
  var desktopSource = source !== null && source !== void 0 && (_source$default = source.default) !== null && _source$default !== void 0 && _source$default.src ? source.default : undefined;
  var tabletSource = source !== null && source !== void 0 && (_source$lg_lte = source.lg_lte) !== null && _source$lg_lte !== void 0 && _source$lg_lte.src ? source.lg_lte : undefined;
  var mobileSource = source !== null && source !== void 0 && (_source$sm_lte = source.sm_lte) !== null && _source$sm_lte !== void 0 && _source$sm_lte.src ? source.sm_lte : undefined;
  var hasBackgroundVideo = desktopSource || tabletSource || mobileSource;
  // If no sources at all, don't render anything
  if (!hasBackgroundVideo) {
    return null;
  }

  // Common video props
  var commonVideoProps = {
    autoPlay: true,
    muted: true,
    loop: true,
    playsInline: true,
    size: size,
    position: position
  };
  var hasMobileVideo = !!mobileSource;
  var hasTabletVideo = !!tabletSource;
  var hasDesktopVideo = !!desktopSource;
  var isVisibleOnDesktop = !!(visible !== null && visible !== void 0 && visible.default);
  var isVisibleOnTablet = !!(visible !== null && visible !== void 0 && visible.lg_lte);
  var isVisibleOnMobile = !!(visible !== null && visible !== void 0 && visible.sm_lte);
  return /*#__PURE__*/React.createElement(React.Fragment, null, hasMobileVideo && /*#__PURE__*/React.createElement(MobileViewportVideo, _extends({}, commonVideoProps, {
    hideOnMobile: !isVisibleOnMobile,
    className: "mobile-video",
    ref: muteVideoOnMountRef
  }), /*#__PURE__*/React.createElement(Source, {
    src: mobileSource.src
  })), hasTabletVideo && /*#__PURE__*/React.createElement(TabletViewportVideo
  // Don't show on mobile if mobile has source, or tablet has source (as tablet source will show on mobile)
  , _extends({
    hideOnMobile: !isVisibleOnMobile || hasMobileVideo,
    hideOnTablet: !isVisibleOnTablet
  }, commonVideoProps, {
    className: "tablet-video",
    ref: muteVideoOnMountRef
  }), /*#__PURE__*/React.createElement(Source, {
    src: tabletSource.src
  })), hasDesktopVideo && /*#__PURE__*/React.createElement(DesktopViewportVideo, _extends({}, commonVideoProps, {
    className: "desktop-video"
    // Even if no mobile video, but tablet has video, don't show on mobile, because tablet video will cascade to show on mobile.
    ,
    hideOnMobile: !isVisibleOnMobile || hasMobileVideo || hasTabletVideo,
    hideOnTablet: !isVisibleOnTablet || hasTabletVideo,
    hideOnDesktop: !isVisibleOnDesktop,
    ref: muteVideoOnMountRef
  }), /*#__PURE__*/React.createElement(Source, {
    src: desktopSource.src
  })));
};

/**
 * This is needed to workaround React not respecting muted true on <video>. It apparently does mount muted, but just without the attribute. This adds the attribute so that it works on various mobile devices.
 * https://github.com/facebook/react/issues/10389
 */
function useMuteVideoOnMountRef() {
  return useCallback(function useMuteVideoOnMountRef(ref) {
    if (!ref) {
      return;
    }
    ref.setAttribute('muted', '');
  }, []);
}
var GroupBackgroundVideo$1 = GroupBackgroundVideo;

export { GroupBackgroundVideo$1 as default };
